/**
 * Profile Settings Page
 * User profile and preferences management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
} from '@mui/material';
import { usePermissions } from '../contexts/PermissionContext';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import {
  saveThemePreference,
  loadThemePreference,
  getThemeNameFromEmail,
} from '../utils/themeSelector';
import { DEFAULT_UI_SCALE } from '../utils/uiScale';
import useUiScale from '../hooks/useUiScale';

function ProfileSettings() {
  const notify = useNotification();
  const { user, setUserThemePreference } = usePermissions();
  const { availableThemes, tenant } = useTenant();
  const [selectedTheme, setSelectedTheme] = useState('auto');
  const { scale, setScale, options: uiScaleOptions } = useUiScale();

  // Load saved preference on mount
  useEffect(() => {
    const savedPreference = loadThemePreference();
    if (savedPreference) {
      setSelectedTheme(savedPreference);
    } else {
      setSelectedTheme('auto');
    }
  }, []);

  const handleThemeChange = (event) => {
    const newTheme = event.target.value;
    setSelectedTheme(newTheme);

    // Save preference
    if (newTheme === 'auto') {
      // Clear preference to use email-based detection
      localStorage.removeItem('userThemePreference');
      setUserThemePreference(null);
    } else {
      saveThemePreference(newTheme);
      setUserThemePreference(newTheme);
    }

    // Show success message
    notify.success('Theme preference saved successfully!');
  };

  const handleScaleChange = (event) => {
    // Persists to localStorage and applies to the root element immediately (no reload).
    setScale(parseFloat(event.target.value));
    notify.success('Display scale saved successfully!');
  };

  // Get auto-detected theme based on email
  const autoDetectedTheme = getThemeNameFromEmail(user?.email);
  const autoDetectedThemeInfo = availableThemes.find(t => t.id === autoDetectedTheme);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Profile Settings
      </Typography>
      <Grid container spacing={3}>
        {/* User Information */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">
                {user?.email || 'Not available'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role
              </Typography>
              <Typography variant="body1">
                {user?.isAdmin ? 'Administrator' : 'User'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Theme Selector */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Theme Preferences
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {autoDetectedThemeInfo && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Auto-detected theme based on your email ({user?.email}):
                <strong> {autoDetectedThemeInfo.name}</strong>
              </Alert>
            )}

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Select Theme
              </FormLabel>
              <RadioGroup
                value={selectedTheme}
                onChange={handleThemeChange}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        Auto (Recommended)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically select theme based on your email domain
                        {autoDetectedThemeInfo && ` (Currently: ${autoDetectedThemeInfo.name})`}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start' }}
                />

                <Divider sx={{ my: 1 }} />

                {availableThemes.map((theme) => (
                  <FormControlLabel
                    key={theme.id}
                    value={theme.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">
                          {theme.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {theme.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: theme.primaryColor,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: theme.secondaryColor,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        </Box>
                      </Box>
                    }
                    sx={{ mb: 2, alignItems: 'flex-start' }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>

        {/* Display Scale */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Display Scale
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Adjust the overall zoom of the application. A smaller scale fits more on screen;
              this applies on every login and is saved for this browser.
            </Typography>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Scale
              </FormLabel>
              <RadioGroup
                row
                value={String(scale)}
                onChange={handleScaleChange}
              >
                {uiScaleOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={String(option.value)}
                    control={<Radio />}
                    label={
                      option.value === DEFAULT_UI_SCALE
                        ? `${option.label} (Default)`
                        : option.label
                    }
                    sx={{ mr: 3 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ProfileSettings;
